import { LineChartGroup } from '@ecosystem/newron-design-system/build/exports/datavis';

import { StatisticsResponseItem } from '../../../shared/services/collections.service';

const getUtcDay = (date: string): number => {
  const parsedDate = new Date(date);
  return Date.UTC(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
};

interface RunningTotal {
  date: number;
  total: number;
}

const getRunningTotal = (data: StatisticsResponseItem[]): RunningTotal[] => {
  if (!data.length) {
    return [];
  }

  const minDay = getUtcDay(data[0].createdAt);
  const maxDay = getUtcDay(data[data.length - 1].createdAt);

  const result = [];

  let total = 0;

  for (let date = minDay; date <= maxDay; date += 86400000) {
    const entries = data.filter(item => getUtcDay(item.createdAt) === date);
    if (entries.length) {
      total += entries.length;
    }

    result.push({
      date,
      total,
    });
  }

  return result;
};

const filterByUserType = (userType: string) => (item: StatisticsResponseItem) => item.userType === userType;
const filterByUserTypes = (userTypes?: string[]) => (item: StatisticsResponseItem) =>
  userTypes === undefined || userTypes.length === 0 || userTypes.includes(item.userType);

const filterByTime = (minUtcDate?: number, maxUtcDate?: number) => (item: RunningTotal) =>
  (!minUtcDate || item.date >= minUtcDate) && (!maxUtcDate || item.date <= maxUtcDate);

interface FilterOptions {
  userTypes?: string[];
  propositions?: string[];
  minDate?: string;
  maxDate?: string;
}

export const getCollectionsChartDataByUserType = (
  data: StatisticsResponseItem[],
  options: FilterOptions
): LineChartGroup[] => {
  const prefiltered = data.filter(filterByUserTypes(options.userTypes));

  // apply the time filter AFTER calculating the running total
  const filterTime = filterByTime(Date.UTC(2024, 0, 1), null);

  return [
    {
      groupName: 'Internal',
      data: getRunningTotal(prefiltered.filter(filterByUserType('internal'))).filter(filterTime),
    },
    {
      groupName: 'Client',
      data: getRunningTotal(prefiltered.filter(filterByUserType('client'))).filter(filterTime),
    },
    {
      groupName: 'Proxy',
      data: getRunningTotal(prefiltered.filter(filterByUserType('proxy'))).filter(filterTime),
    },
  ];
};

export const getCollectionsChartDataByProposition = (
  data: StatisticsResponseItem[],
  options: FilterOptions
): LineChartGroup[] => {
  const prefiltered = data.filter(filterByUserTypes(options.userTypes)).map(item => ({
    ...item,
    // for later processing: an array of distinct propositions
    propositions: Array.from(
      new Set(
        item.insights
          .filter(i => !['platform'].includes(i.proposition))
          // link was renamed to playground in the backend
          .map(i => (i.proposition === 'link' ? 'playground' : i.proposition))
      )
    ).sort((a, b) => a.localeCompare(b)),
  }));

  const groups = prefiltered.reduce(
    (acc, curr) => {
      const groupName = curr.propositions.length > 0 ? curr.propositions.join(' + ') : '(none)';

      acc[groupName] ??= [];
      acc[groupName].push(curr);

      return acc;
    },
    {} as Record<string, StatisticsResponseItem[]>
  );

  // apply the time filter AFTER calculating the running total
  const byTime = filterByTime(Date.UTC(2024, 0, 1), null);

  return (
    Object.keys(groups)
      // sort by number of propositions first, then alphabetically
      .sort((a, b) => a.split(' + ').length - b.split(' + ').length || a.localeCompare(b))
      .map(groupName => ({
        groupName,
        data: getRunningTotal(groups[groupName]).filter(byTime),
      }))
      .filter(g => g.data.length > 0)
  );

  // const bySingleProposition = (proposition: string) => item =>
  //   item.propositions.length === 1 && item.propositions[0] === proposition;

  // const byPropositions = (propositions: string[]) => item =>
  //   item.propositions.length === propositions.length && propositions.every(p => item.propositions.includes(p));

  // return [
  //   {
  //     groupName: 'Consumer',
  //     data: getRunningTotal(prefiltered.filter(byPropositions(['consumer']))).filter(byTime),
  //   },
  //   {
  //     groupName: 'Market',
  //     data: getRunningTotal(prefiltered.filter(byPropositions(['market']))).filter(byTime),
  //   },
  //   {
  //     groupName: 'Retail',
  //     data: getRunningTotal(prefiltered.filter(bySingleProposition('retail'))).filter(byTime),
  //   },
  //   {
  //     groupName: 'Market + Consumer',
  //     data: getRunningTotal(prefiltered.filter(byPropositions(['market', 'consumer']))).filter(byTime),
  //   },
  //   {
  //     groupName: 'Mixed',
  //     data: getRunningTotal(prefiltered.filter(c => c.propositions.length > 1)).filter(byTime),
  //   },
  // ];
};
